// User-picker cache — loaded lazily once per session.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { UserPublic } from "@juuplan/shared";
import { api } from "../lib/api";

export interface UsersState {
  loading: boolean;
  items: UserPublic[];
  error: string | null;
}

const initial: UsersState = { loading: true, items: [], error: null };
const usersSignal: Signal<UsersState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadUsers(): Promise<void> {
  try {
    const items = await api<UserPublic[]>("/api/users");
    usersSignal.value = { loading: false, items, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    usersSignal.value = { loading: false, items: [], error: message };
  } finally {
    inFlight = null;
  }
}

export function useUsers(): UsersState {
  useEffect(() => {
    if (!inFlight && usersSignal.value.loading) {
      inFlight = loadUsers();
    }
  }, []);
  return usersSignal.value;
}

export function findUser(id: number | null | undefined): UserPublic | null {
  if (id === null || id === undefined) return null;
  return usersSignal.value.items.find((u) => u.id === id) ?? null;
}
